import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  fetchAdminPvpCampaign,
  fetchAdminCampaignStats,
  fetchCampaignLeaderboard,
  fetchCampaignLobbyRooms,
  finalizeAdminPvpCampaign,
  formatIsoDate,
} from '../../lib/pvpCampaignApi';
import { usePortalT } from '../../i18n/usePortalT';
import {
  PageTitle,
  PageSubtitle,
  Card,
  Btn,
  BtnRow,
  BtnDanger,
  ErrorText,
  StatGrid,
  StatCard,
  StatLabel,
  StatValue,
  Badge,
  Table,
} from '../../styles/adminStyles';

export default function CampaignDetailPage() {
  const { id } = useParams();
  const { t, tEnum, language } = usePortalT();
  const zh = language === 'zh';
  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ending, setEnding] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await fetchAdminPvpCampaign(id);
      const [s, lb, rm] = await Promise.all([
        fetchAdminCampaignStats(id),
        fetchCampaignLeaderboard(c.slug, 100),
        fetchCampaignLobbyRooms(c.slug),
      ]);
      setCampaign(c);
      setStats(s);
      setLeaderboard(lb);
      setRooms(rm);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleEnd = async () => {
    if (!window.confirm(t('campaigns.confirmEnd'))) return;
    setEnding(true);
    const out = await finalizeAdminPvpCampaign(id);
    setEnding(false);
    if (!out.success) {
      alert(out.error || t('campaigns.endFailed'));
      return;
    }
    await reload();
  };

  const exportLeaderboard = () => {
    const header = 'rank,userId,displayName,points,matchesPlayed,matchesWon,mvpCount,captures,buries';
    const lines = leaderboard.map((r) =>
      [r.rank, r.userId, JSON.stringify(r.displayName), r.points, r.matchesPlayed, r.matchesWon, r.mvpCount, r.captures, r.buries].join(','),
    );
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${campaign?.slug || 'campaign'}-leaderboard.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <p>{t('common.loading')}</p>;
  if (error) return <ErrorText>{error}</ErrorText>;
  if (!campaign) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <PageTitle style={{ margin: 0, flex: 1 }}>{campaign.titleZh}</PageTitle>
        <Badge $status={campaign.status}>{tEnum('campaignStatus', campaign.status)}</Badge>
      </div>
      <PageSubtitle>
        {campaign.titleEn} · <code>{campaign.slug}</code>
      </PageSubtitle>

      <BtnRow style={{ marginTop: 0, marginBottom: 20 }}>
        <Link to={`/admin/campaigns/${id}/edit`}><Btn $primary>{t('common.edit')}</Btn></Link>
        <Btn type="button" onClick={exportLeaderboard} disabled={leaderboard.length === 0}>{t('campaigns.exportCsv')}</Btn>
        <Btn type="button" onClick={() => void reload()}>{t('common.refresh')}</Btn>
        {campaign.status !== 'ended' && campaign.status !== 'rewards_distributed' ? (
          <BtnDanger type="button" disabled={ending} onClick={() => void handleEnd()}>
            {ending ? t('common.processing') : t('campaigns.endEvent')}
          </BtnDanger>
        ) : null}
        <Link to="/admin"><Btn type="button">{t('common.backToList')}</Btn></Link>
      </BtnRow>

      <StatGrid>
        <StatCard>
          <StatLabel>{t('campaigns.colParticipants')}</StatLabel>
          <StatValue>{stats?.participantCount ?? 0}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{t('campaigns.colMatches')}</StatLabel>
          <StatValue>{stats?.totalMatches ?? 0}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{t('campaigns.colPoints')}</StatLabel>
          <StatValue>{stats?.totalPointsAwarded ?? 0}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{t('campaigns.lobbyRooms')}</StatLabel>
          <StatValue>{stats?.activeLobbyRooms ?? 0}</StatValue>
        </StatCard>
      </StatGrid>

      <Card style={{ marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>{t('campaigns.info')}</h3>
        <p style={{ margin: '0 0 8px', fontSize: 13, color: '#475569' }}><strong>{t('campaigns.start')}</strong>{formatIsoDate(campaign.startsAt, zh)}</p>
        <p style={{ margin: '0 0 8px', fontSize: 13, color: '#475569' }}><strong>{t('campaigns.end')}</strong>{formatIsoDate(campaign.endsAt, zh)}</p>
        <p style={{ margin: '0 0 8px', fontSize: 13, color: '#475569' }}><strong>{t('campaigns.lastMatch')}</strong>{stats?.lastMatchAt ? formatIsoDate(stats.lastMatchAt, zh) : '—'}</p>
        <p style={{ margin: '12px 0 0', fontSize: 14, lineHeight: 1.6 }}>{campaign.descriptionZh || t('campaigns.noZhDesc')}</p>
        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{campaign.descriptionEn}</p>
      </Card>

      <Card style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <h3 style={{ margin: 0, padding: '16px 20px', fontSize: 15, borderBottom: '1px solid #ececef' }}>
          {t('campaigns.leaderboard')} ({leaderboard.length})
        </h3>
        {leaderboard.length === 0 ? (
          <p style={{ padding: 20, color: '#64748b' }}>{t('campaigns.noPoints')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>#</th>
                <th>{t('campaigns.colUser')}</th>
                <th>{t('campaigns.colPoints')}</th>
                <th>{t('campaigns.colPlayed')}</th>
                <th>{t('campaigns.colWon')}</th>
                <th>MVP</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((r) => (
                <tr key={r.userId}>
                  <td>{r.rank}</td>
                  <td>{r.displayName || r.userId}</td>
                  <td><strong>{r.points}</strong></td>
                  <td>{r.matchesPlayed}</td>
                  <td>{r.matchesWon}</td>
                  <td>{r.mvpCount}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <h3 style={{ margin: 0, padding: '16px 20px', fontSize: 15, borderBottom: '1px solid #ececef' }}>
          {t('campaigns.lobbyRooms')} ({rooms.length})
        </h3>
        {rooms.length === 0 ? (
          <p style={{ padding: 20, color: '#64748b' }}>{t('campaigns.noRooms')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t('campaigns.colRoomId')}</th>
                <th>{t('campaigns.colRoomName')}</th>
                <th>{t('campaigns.colMode')}</th>
                <th>{t('campaigns.colMembers')}</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.id}</td>
                  <td>{r.roomName || '—'}</td>
                  <td>{r.mode || '—'}</td>
                  <td>{r.memberCount ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
